import { useState, useEffect, useCallback } from 'react'
import { RefreshCw, AlertTriangle, X } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import './SponsorManager.css'

const ENTITY_LABELS = {
  zones: 'Zones',
  ads: 'Ads',
  groups: 'Groups',
  advertisers: 'Advertisers',
}

// Backend normalizes AdSpeed *.getCounts into { total, active, pending, archived, deleted }.
// Older payloads or stale-cache rows may still be raw — keep fallbacks for safety.
function extractTotal(raw) {
  if (raw == null) return '—'
  if (typeof raw === 'number') return raw
  if (typeof raw === 'string') return raw
  return raw.total ?? raw.Count ?? raw.TotalCount ?? raw.count ?? '—'
}
function extractBreakdown(raw) {
  if (!raw || typeof raw !== 'object') return null
  const parts = []
  if (raw.active != null)   parts.push(`${raw.active} active`)
  if (raw.pending)          parts.push(`${raw.pending} pending`)
  if (raw.archived)         parts.push(`${raw.archived} archived`)
  if (raw.deleted)          parts.push(`${raw.deleted} deleted`)
  return parts.length ? parts.join(' · ') : null
}

export default function SponsorManager() {
  const { api } = useAuth()
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [status, setStatus] = useState('')

  const loadSummary = useCallback(async () => {
    try {
      const data = await api('/sponsors/summary')
      setSummary(data)
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }, [api])

  useEffect(() => {
    setLoading(true)
    loadSummary().finally(() => setLoading(false))
  }, [loadSummary])

  async function handleRefresh() {
    setRefreshing(true)
    try {
      await api('/sponsors/cache/bust?prefix=summary:', { method: 'POST' })
      await loadSummary()
      setStatus('Refreshed from AdSpeed')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setRefreshing(false)
  }

  const counts = summary?.counts || {}
  const errors = summary?.errors || {}
  const recent = summary?.recent || []

  return (
    <div className="sponsors-page">
      <div className="sponsors-header">
        <div className="sponsors-header-left">
          <h1>Sponsors</h1>
          <span className="sponsors-count">AdSpeed integration</span>
        </div>
        <div className="sponsors-header-right">
          <button
            className="sponsors-refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing || loading}
            title="Bust cache and refetch from AdSpeed"
          >
            <RefreshCw size={14} className={refreshing ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {status && (
        <div className={`sponsors-status ${status.startsWith('Error') ? 'error' : 'info'}`}>
          <span>{status}</span>
          <button onClick={() => setStatus('')}><X size={14} /></button>
        </div>
      )}

      {loading ? (
        <div className="muted">Loading sponsor counts…</div>
      ) : (
        <>
          <div className="sponsors-dashboard-grid">
            {Object.entries(ENTITY_LABELS).map(([key, label]) => {
              const raw = counts[key]
              const breakdown = extractBreakdown(raw)
              return (
                <div className="sponsors-tile" key={key}>
                  <div className="sponsors-tile-label">{label}</div>
                  <div className="sponsors-tile-value">{extractTotal(raw)}</div>
                  {breakdown && (
                    <div className="sponsors-tile-sub">{breakdown}</div>
                  )}
                  {errors[key] && (
                    <div className="sponsors-tile-sub">
                      <span className="sponsors-stale-badge">
                        <AlertTriangle size={11} /> {errors[key]}
                      </span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <div className="sponsors-audit-section">
            <h2>Recent activity</h2>
            {recent.length === 0 ? (
              <div className="sponsors-audit-empty">No recorded activity yet.</div>
            ) : (
              <table className="sponsors-audit-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Actor</th>
                    <th>Action</th>
                    <th>Entity</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map(row => (
                    <tr key={row.id}>
                      <td>{row.timestamp}</td>
                      <td>{row.actor || '—'}</td>
                      <td>{row.action}</td>
                      <td>{row.entity_type ? `${row.entity_type}:${row.entity_id ?? ''}` : '—'}</td>
                      <td className={row.result === 'ok' ? 'result-ok' : 'result-error'}>
                        {row.result === 'ok' ? 'ok' : (row.error_message || 'error')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  )
}
